import React, { memo, useState, useEffect, useRef } from 'react';

const getPoint = (idx, center, radius, totalPoints = 250) => {
  const angle = (idx * 360 / totalPoints);
  const rad = (angle * Math.PI) / 180;
  return {
    x: center + radius * Math.cos(rad),
    y: center + radius * Math.sin(rad),
  };
};

const buildSvgMarkup = (positions, size, strokeWidth, totalPoints = 250) => {
  const radius = (size / 2) - 2;
  const center = size / 2;

  const refPoints = [0, Math.round(totalPoints/4), Math.round(totalPoints/2), Math.round(totalPoints*3/4)]
    .map(idx => {
      const p = getPoint(idx, center, radius, totalPoints);
      return `<circle cx="${p.x}" cy="${p.y}" r="2" fill="#e2e8f0"/>`;
    })
    .join('');

  const lines = (positions || [])
    .slice(1)
    .map((pos, i) => {
      const start = getPoint(positions[i], center, radius, totalPoints);
      const end = getPoint(pos, center, radius, totalPoints);
      return `<line x1="${start.x}" y1="${start.y}" x2="${end.x}" y2="${end.y}" stroke="black" stroke-width="${strokeWidth}" stroke-opacity="0.9"/>`;
    })
    .join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <circle cx="${center}" cy="${center}" r="${radius}" fill="white" stroke="#f1f5f9" stroke-width="1"/>
    ${refPoints}
    ${lines}
  </svg>`;
};

// ─── SequenceSnapshot ────────────────────────────────────────────────────────
// Genera el diseño una sola vez en un canvas offscreen y lo muestra como <img>.
// Usa localStorage para cachear el resultado entre recargas.
export const SequenceSnapshot = memo(({ secId, positions, size = 190, strokeWidth = 0.05 }) => {
  const cacheKey = `seq_snap_${secId}`;
  const [dataUrl, setDataUrl] = useState(() => localStorage.getItem(cacheKey));
  const generatingRef = useRef(false);

  useEffect(() => {
    if (dataUrl || generatingRef.current || !positions?.length) return;
    generatingRef.current = true;

    const detectedTotal = positions.length > 0 ? Math.max(...positions) + 1 : 250;
    const svg = buildSvgMarkup(positions, size, strokeWidth, detectedTotal);
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);

      try {
        const png = canvas.toDataURL('image/png');
        localStorage.setItem(cacheKey, png);
        setDataUrl(png);
      } catch (e) {
        // Si localStorage está lleno, solo mostramos sin guardar
        const png = canvas.toDataURL('image/png');
        setDataUrl(png);
      }
      generatingRef.current = false;
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      generatingRef.current = false;
    };

    img.src = url;
  }, [secId, positions, size, strokeWidth, cacheKey, dataUrl]);

  if (!dataUrl) {
    return (
      <div
        style={{ width: size, height: size }}
        className="rounded-full bg-white flex flex-col items-center justify-center gap-1 opacity-30"
      >
        <div className="w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
        <span className="text-[8px] font-bold uppercase tracking-tighter">Procesando...</span>
      </div>
    );
  }

  return (
    <img
      src={dataUrl}
      alt="Previsualización de secuencia"
      width={size}
      height={size}
      className="rounded-full object-cover"
      draggable={false}
    />
  );
});

SequenceSnapshot.displayName = 'SequenceSnapshot';

// ─── SequencePreview (SVG en vivo, para uso en el hilador activo) ────────────
const SequencePreview = memo(({ positions, size = 120, active = false, strokeWidth = 0.05 }) => {
  const [isReady, setIsReady] = useState(false);
  const RADIUS = (size / 2) - 2;
  const CENTER = size / 2;

  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{ width: size, height: size }} className="relative flex items-center justify-center bg-white rounded-full overflow-hidden">
      {!isReady ? (
        <div className="flex flex-col items-center justify-center gap-1 opacity-20">
          <div className="w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-[8px] font-bold uppercase tracking-tighter">Procesando...</span>
        </div>
      ) : (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible animate-in fade-in duration-500">
          <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="white" stroke="#f1f5f9" strokeWidth="1" />
          {[0, 62, 125, 187].map(idx => {
            const p = getPoint(idx, CENTER, RADIUS);
            return <circle key={idx} cx={p.x} cy={p.y} r={2} fill="#e2e8f0" />;
          })}
          <g>
            {positions && positions.map((pos, i) => {
              if (i === 0) return null;
              const start = getPoint(positions[i - 1], CENTER, RADIUS);
              const end = getPoint(pos, CENTER, RADIUS);
              return (
                <line
                  key={i}
                  x1={start.x} y1={start.y}
                  x2={end.x} y2={end.y}
                  stroke="black"
                  strokeWidth={strokeWidth}
                  strokeOpacity="0.9"
                />
              );
            })}
          </g>
        </svg>
      )}
    </div>
  );
});

SequencePreview.displayName = 'SequencePreview';

export default SequencePreview;

